import React from "react";
import Box from "@mui/material/Box";
import { apiUrls } from "../../actprep-ui/utils/urls";
import PaginatedTable from "../../actprep-ui/components/PaginatedTable";
import { useFilters } from "../../actprep-ui/hooks/ui";
import { Drawer } from "@mui/material";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import layout from "../../actprep-ui/constants/layout";
import useResponsiveValue from "../../actprep-ui/hooks/ui/useResponsiveValue";
import { dataGridNestedColumnGetter } from "../../actprep-ui/utils/tables";
import { sortingModes } from "../../actprep-ui/constants/dataGrid";
import WeekStats from "../../actprep-ui/components/WeekStats";
import { filterTypes } from "../../actprep-ui/constants/ui";
import dayjs from "dayjs";
import { groups } from "../../actprep-ui/constants/auth";

// Validator scaffolding - implement the lambda bodies as needed
const productivityValidators = {
  total_check_ins: (val) => (val == 7 ? "green" : "red"),
  total_check_ins_on_time: (val) => (val >= 5 ? "green" : "red"),
  total_time_submissions: (val) => (val == 7 ? "green" : "red"),
  total_time_submissions_on_time: (val) => {
    return val >= 5 ? "green" : "red";
  },
  total_hours_worked: (val) => (val >= 10 ? "green" : "red"),
};

export default function ProductivitySheet() {
  const filterDisc = useDisclosure();
  const { Filters, FiltersAppliedList, getFilterParams } = useFilters({
    name: "Productivity Sheet",
    saveState: true,
    filtersConfig: [
      {
        field: "start_date",
        type: filterTypes.DATE_PICKER,
        props: {
          label: "Start Date",
          default: dayjs().add(-1 * dayjs().day(), "day"),
          DatePickerProps: {
            maxDate: dayjs().day(5),
          },
        },
      },
      {
        field: "end_date",
        type: filterTypes.DATE_PICKER,
        props: {
          label: "End Date",
          default: dayjs()
            .add(-1 * dayjs().day(), "day")
            .add(6, "day"),
          DatePickerProps: {
            maxDate: dayjs().day(6),
          },
        },
      },
      {
        field: "user__groups__name",
        type: filterTypes.SELECT,
        props: {
          label: "User Group",
          options: [
            {
              label: "All",
              value: "",
            },
            ...Object.values(groups).map((groupName) => ({
              value: groupName,
              label: groupName,
              default: groupName === groups.EMPLOYEES,
            })),
          ],
        },
      },
    ],
    handleClose: filterDisc.onClose,
  });
  const columnWidthFactor = useResponsiveValue({
    xs: 0.5,
    md: 1,
  });

  const columns = [
    {
      field: "user.first_name",
      headerName: "First name",
      width: 100,
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "user.last_name",
      headerName: "Last name",
      width: 100,
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "total_check_ins",
      headerName: "Check-ins",
      width: 100,
      type: "number",
    },
    {
      field: "total_check_ins_on_time",
      headerName: "On-time Check-ins",
      width: 120,
      type: "number",
    },
    {
      field: "total_time_submissions",
      headerName: "Hours Submissions",
      width: 130,
      type: "number",
    },
    {
      field: "total_time_submissions_on_time",
      headerName: "On-time Hours Submissions",
      width: 140,
      type: "number",
    },
    {
      field: "total_hours_worked",
      headerName: "Hours Worked",
      width: 120,
      type: "number",
      valueFormatter: (val) => parseFloat(val || 0).toFixed(2),
    },
    {
      field: "todo_items_completed",
      headerName: "Todo Items Completed",
      width: 120,
      type: "number",
    },
    {
      field: "schedule_items_completed",
      headerName: "Schedule Items Completed",
      width: 120,
      type: "number",
    },
  ];

  const { start_date: startDate, end_date: endDate } = getFilterParams();
  const isWeek = dayjs(endDate).diff(dayjs(startDate), "day") === 6;
  return (
    <>
      <Drawer anchor="right" {...filterDisc.getDisclosureProps()}>
        <Box
          sx={{ paddingTop: `${layout.navBarHeight + 1}rem`, width: "25rem" }}
        >
          <Filters />
        </Box>
      </Drawer>
      <PaginatedTable
        columnWidthFactor={columnWidthFactor}
        tableName="productivity-sheet"
        columns={columns}
        searchable
        apiUrl={apiUrls.admin.productivity()}
        disableCellFocus
        urlParams={{
          search_params: getFilterParams(),
          retry: false,
        }}
        initialState={{
          pinnedColumns: {
            right: ["actions"],
          },
        }}
        sortingMode={sortingModes.SERVER}
        onFiltersClick={filterDisc.onToggle}
        FiltersAppliedList={FiltersAppliedList}
        disableColumnMenu
        getRowTitle={({ row }) =>
          `${row.user?.first_name} ${row.user?.last_name}`
        }
        getCellClassName={
          isWeek
            ? (params) => {
                const field = params.colDef.field;
                const validator = productivityValidators[field];
                if (!validator) {
                  return "";
                }
                const result = validator(params.value, params.row);
                const allowed = ["green", "yellow", "red"];
                return allowed.includes(result)
                  ? `${result}-flag productivity-field`
                  : "productivity-field";
              }
            : undefined
        }
        sx={{
          "& .MuiDataGrid-cell.productivity-field.green-flag": {
            backgroundColor: "green",
          },
          "& .MuiDataGrid-cell.productivity-field.red-flag": {
            backgroundColor: "red",
          },
          "& .MuiDataGrid-cell.productivity-field.yellow-flag": {
            backgroundColor: "yellow",
          },
        }}
        getDetailPanelContent={({ row }) => (
          <Box
            sx={{
              p: 2,
              backgroundColor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <WeekStats
              embeddedMode
              userProfileId={row.uuid}
              startDate={getFilterParams().start_date}
              endDate={getFilterParams().end_date}
            />
          </Box>
        )}
        getDetailPanelHeight={() => "auto"}
      />
    </>
  );
}
